import { History, Location } from 'history'
import React from 'react'
import { connect } from 'react-redux'
import { match } from 'react-router-dom'
import { Grid, Image } from 'semantic-ui-react'
import trialytix_login from '../../../assets/svg/trialytix_login.svg'
import { toastMsg } from '../../../components/Toast/toastMsg'
import { RootState } from '../../../store/rootState'
import { ICredentials, IEmailSuccess, IEmailVerification, IPasswordResetCredentilas, IUser } from '../../../types/auth'
import { IUserDetail } from '../../../types/user'
import * as LoginActions from '../actions/authActions'
import * as notificationActions from '../../notifications/actions/notificationActions'
import ForgetPasswordComponent from '../components/ForgetPasswordComponent'
import ForgetPasswordEmail from '../components/ForgetPasswordEmail'
import LoginComponent from '../components/LoginComponent'
import { IResetConf } from '../types/resetConf'

interface OwnProps {
  location: Location
  history: History
  match: match
}

interface StateProps {
  user: IUser | null
  route: string
  resetConf: IPasswordResetCredentilas
}

interface DispatchProps {
  login: (credentials: ICredentials) => Promise<IUserDetail>
  resetPassword: (reset: string) => void
  resetPasswordEmailVerification: (msg: IEmailVerification) => Promise<IEmailSuccess>
  setPasswordResetCredentilas: (resetConf: IResetConf) => void
  makeNewPassword: (resetCredentials: IPasswordResetCredentilas) => Promise<IPasswordResetCredentilas>

  getNotifications: () => void
}

type Props = StateProps & OwnProps & DispatchProps

const LoginContainer = (props: Props): JSX.Element => {
  const login = (credentials: ICredentials) => {
    props.login(credentials).then(() => {
      props.getNotifications()
      // Always redirect to "/" and let AppRouter handle the final redirect
      // based on feature flags (restricted vs regular mode)
      props.history.replace('/')
    })
  }

  const resetPassword = (route: string) => {
    props.resetPassword(route)
  }

  const sendEmail = (email: IEmailVerification) => {
    if (email.email !== '') {
      props.resetPasswordEmailVerification(email).then(() => props.resetPassword('/login'))
    } else {
      toastMsg('Email format invalid.', 'ERROR', 'error')
    }
  }

  const sendResetPassword = (resetConf: IPasswordResetCredentilas) => {
    if (resetConf.password === resetConf.passwordTwo && resetConf.password !== '') {
      props.makeNewPassword(resetConf).then(() => {
        const resetConf: IResetConf = { password: '', uidb64: '', token: '' }
        props.setPasswordResetCredentilas(resetConf)
        props.resetPassword('/login')
      })
    } else {
      toastMsg('Passwords not the same or empty.', 'ERROR', 'error')
    }
    return props.resetConf
  }

  const _component = () => {
    if (props.route === '/login') {
      return <LoginComponent login={login} resetPassword={resetPassword} />
    }
    if (props.route === '/reset-password') {
      return <ForgetPasswordEmail sendEmail={sendEmail} resetPassword={resetPassword} />
    }
    if (props.route === '/passwords-part') {
      return (
        <ForgetPasswordComponent
          resetConfObj={props.resetConf}
          forgetPassword={sendResetPassword}
          resetPassword={resetPassword}
        />
      )
    }
  }

  return (
    <Grid textAlign='center' style={{ height: '100vh', width: '100%', margin: 0 }} verticalAlign='middle'>
      <Grid.Row style={{ background: 'linear-gradient(0deg, #4AE2B9 -7.43%, #475A91 60.81%, #191F3B 100%)', width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Image src={trialytix_login} style={{position: 'absolute', top:'2.5rem', left:'2.71rem'}}/>
        {_component()}
      </Grid.Row>
    </Grid>
  )
}

const mapStateToProps = (state: RootState): StateProps => {
  return {
    user: state.authState.user,
    route: state.authState.pubPath,
    resetConf: state.authState.resetConf,
  }
}

const mapDispatchToProps = (
  dispatch:
    LoginActions.AuthThunkDispatchType
    & notificationActions.NotificationsThunkDispatchType
): DispatchProps => {
  return {
    login: async (credentials) => dispatch(LoginActions.login(credentials)),
    resetPassword: (route) => dispatch(LoginActions.resetPassword(route)),
    resetPasswordEmailVerification: async (email) => dispatch(LoginActions.resetPasswordEmailVerification(email)),
    setPasswordResetCredentilas: (resetConf) => dispatch(LoginActions.setResetConf(resetConf)),
    makeNewPassword: async (resetCredential) => dispatch(LoginActions.makeNewPassword(resetCredential)),

    getNotifications: () => dispatch(notificationActions.getNotifications()),
  }
}

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  mapStateToProps,
  mapDispatchToProps
)(LoginContainer)
